/* Offer card — what's been pitched on this call (Presented) and what
 * the eligibility rules still allow (Available to pitch). The latter
 * gives the human agent an action surface: clicking "Pitch this" fires
 * a nudge into the live LLM context so the AI surfaces that offer next.
 *
 * Both lists refresh on the existing call SSE so they stay current as
 * the AI fires make_offer / accept_offer / reject_offer tools.
 *
 * Future phase: offer-ladder (lead/fallback/last-resort) banding + a
 * bill-impact diff card. For now this is one priority-sorted list. */

import { useEffect, useMemo, useState } from "react";

import { API_URL, fmtTime } from "../lib/api";
import { agentHeaders, useAgentIdentity } from "./identity";

type GrantStatus = "offered" | "accepted" | "rejected" | "expired";

type Grant = {
  id: number;
  status: GrantStatus;
  offered_at: string;
  resolved_at: string | null;
  offer_id: number;
  offer_code: string;
  offer_name: string;
  discount_percent: number | null;
  discount_cents: number | null;
  free_months: number | null;
  duration_months: number | null;
};

export type EligibleOffer = {
  id: number;
  code: string;
  name: string;
  description: string | null;
  discount_percent: number | null;
  discount_cents: number | null;
  free_months: number | null;
  upgrade_to_plan: string | null;
  duration_months: number | null;
  priority: number;
};

const STATUS_LABEL: Record<GrantStatus, string> = {
  offered:  "Presented",
  accepted: "Accepted",
  rejected: "Declined",
  expired:  "Expired",
};

export type Terms = {
  discount_percent: number | null;
  discount_cents: number | null;
  free_months: number | null;
  duration_months: number | null;
};

export function describeTerms(t: Terms, kind?: string | null): string {
  const parts: string[] = [];
  if (t.discount_percent != null) parts.push(`${t.discount_percent}% off`);
  if (t.discount_cents != null) parts.push(`$${(t.discount_cents / 100).toFixed(0)} off`);
  if (t.free_months != null) parts.push(`${t.free_months} free month${t.free_months === 1 ? "" : "s"}`);
  if (t.duration_months != null) parts.push(`${t.duration_months}-month term`);
  return parts.join(" · ") || kind || "—";
}

// Rung labels mirror the v4 mockup: top-priority offer is the LEAD,
// next is FALLBACK, anything below is LAST-RESORT. The order is by
// the ELIGIBLE_OFFERS_SQL `priority DESC` which the retention team
// already curates per offer.
export type Rung = "lead" | "fallback" | "last-resort";

export function rungAt(index: number): Rung {
  if (index === 0) return "lead";
  if (index === 1) return "fallback";
  return "last-resort";
}

export const RUNG_LABEL: Record<Rung, string> = {
  lead:           "LEAD",
  fallback:       "FALLBACK",
  "last-resort":  "LAST RESORT",
};

// Compute the customer's bill after a given offer is applied. Mirrors
// the discount math the bot's pricing prompts use: percent first, then
// flat-cents, clamped at $0. free_months returns the discounted
// monthly value (the "during the term" rate) — front-end is honest
// about not modelling the post-term snap-back.
export function applyOfferToMonthly(
  currentCents: number,
  o: Terms,
): { afterCents: number; freeMonthsApplied: boolean } {
  let after = currentCents;
  if (o.discount_percent != null) {
    after = Math.round(after * (1 - o.discount_percent / 100));
  }
  if (o.discount_cents != null) {
    after = after - o.discount_cents;
  }
  if (after < 0) after = 0;
  return {
    afterCents: after,
    freeMonthsApplied: (o.free_months ?? 0) > 0,
  };
}

function fmtMoney(cents: number): string {
  return `$${(cents / 100).toFixed(0)}`;
}

function BillImpact({
  impact,
}: {
  impact: {
    current: number;
    after: number;
    deltaMonthly: number;
    deltaAnnual: number;
  };
}) {
  return (
    <div className="agent-desk-offer-row-impact">
      <span className="agent-desk-offer-row-impact-from">{fmtMoney(impact.current)}/mo</span>
      <span className="agent-desk-offer-row-impact-arrow">→</span>
      <span className="agent-desk-offer-row-impact-to">{fmtMoney(impact.after)}/mo</span>
      <span className="agent-desk-offer-row-impact-delta">
        ↓{fmtMoney(impact.deltaMonthly)}/mo · ↓{fmtMoney(impact.deltaAnnual)}/yr
      </span>
    </div>
  );
}

function billImpact(
  currentCents: number | null,
  o: Terms,
): { current: number; after: number; deltaMonthly: number; deltaAnnual: number } | null {
  if (currentCents == null || currentCents <= 0) return null;
  if (
    o.discount_percent == null &&
    o.discount_cents == null &&
    (o.free_months ?? 0) === 0
  ) {
    // Nothing to show — offer doesn't move the bill (e.g. plan upgrade).
    return null;
  }
  const { afterCents } = applyOfferToMonthly(currentCents, o);
  const deltaMonthly = currentCents - afterCents;
  const deltaAnnual = deltaMonthly * 12;
  return {
    current: currentCents,
    after: afterCents,
    deltaMonthly,
    deltaAnnual,
  };
}

export function OfferCard({
  callId,
  currentMonthlyCents,
}: {
  callId: number;
  currentMonthlyCents: number | null;
}) {
  const { identity } = useAgentIdentity();
  const [grants, setGrants] = useState<Grant[]>([]);
  const [available, setAvailable] = useState<EligibleOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [pitching, setPitching] = useState<number | null>(null);
  const [pitchErr, setPitchErr] = useState<string | null>(null);
  const [resolving, setResolving] = useState<number | null>(null);
  const [resolveErr, setResolveErr] = useState<string | null>(null);

  // Keep the lists deduped — if a Presented row exists for an offer, hide
  // it from the Available section (the SSE refresh below races with
  // optimistic UI updates so the same offer can briefly appear twice).
  const presentedOfferIds = useMemo(
    () => new Set(grants.map((g) => g.offer_id)),
    [grants],
  );
  const availableFiltered = useMemo(
    () => available.filter((o) => !presentedOfferIds.has(o.id)),
    [available, presentedOfferIds],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);

    async function refresh() {
      try {
        const [grantsRes, availableRes] = await Promise.all([
          fetch(`${API_URL}/api/calls/${callId}/offer-grants`),
          fetch(`${API_URL}/api/calls/${callId}/eligible-offers`),
        ]);
        if (!grantsRes.ok) throw new Error(`offer-grants ${grantsRes.status}`);
        if (!availableRes.ok) throw new Error(`eligible-offers ${availableRes.status}`);
        const gData = (await grantsRes.json()) as { grants: Grant[] };
        const aData = (await availableRes.json()) as { offers: EligibleOffer[] };
        if (!cancelled) {
          setGrants(gData.grants ?? []);
          setAvailable(aData.offers ?? []);
        }
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void refresh();

    // The grant + eligibility tables don't have their own SSE channels,
    // but the call SSE pings on every row update — so we re-fetch on
    // any call event. Cheap; usually handful of rows. Keeps the card
    // in sync without dedicated wiring.
    const es = new EventSource(`${API_URL}/api/calls/${callId}/stream`);
    es.addEventListener("call", () => {
      void refresh();
    });
    es.addEventListener("tool_call", () => {
      void refresh();
    });
    return () => {
      cancelled = true;
      es.close();
    };
  }, [callId]);

  async function resolveGrant(grantId: number, status: "accepted" | "rejected") {
    if (resolving !== null) return;
    setResolving(grantId);
    setResolveErr(null);
    try {
      const r = await fetch(`${API_URL}/api/offer-grants/${grantId}`, {
        method: "PATCH",
        headers: {
          "content-type": "application/json",
          ...agentHeaders(identity),
        },
        body: JSON.stringify({ status }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        const detail =
          (data as { detail?: string; error?: string }).detail ??
          (data as { detail?: string; error?: string }).error ??
          `grant update failed (${r.status})`;
        setResolveErr(detail);
        return;
      }
      // Optimistic — flip locally so the agent sees it instantly. SSE
      // refresh will reconcile the timestamp from the worker.
      setGrants((cur) =>
        cur.map((g) =>
          g.id === grantId
            ? { ...g, status, resolved_at: new Date().toISOString() }
            : g,
        ),
      );
    } catch (e) {
      setResolveErr(e instanceof Error ? e.message : String(e));
    } finally {
      setResolving(null);
    }
  }

  async function pitchOffer(offer: EligibleOffer) {
    if (pitching !== null) return;
    setPitching(offer.id);
    setPitchErr(null);
    const terms = describeTerms(offer);
    const text =
      `Pitch the "${offer.name}" offer next (code ${offer.code}: ${terms}). ` +
      `Mention it directly to the customer and use make_offer with offer_code="${offer.code}".`;
    try {
      const r = await fetch(`${API_URL}/api/calls/${callId}/nudge`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          ...agentHeaders(identity),
        },
        body: JSON.stringify({ text }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        const detail =
          (data as { detail?: string; error?: string }).detail ??
          (data as { detail?: string; error?: string }).error ??
          `nudge failed (${r.status})`;
        setPitchErr(detail);
        return;
      }
      // Optimistically drop the offer from Available so the agent sees
      // the click registered. SSE refresh will reconcile when the AI's
      // make_offer tool fires and the grant appears.
      setAvailable((cur) => cur.filter((o) => o.id !== offer.id));
    } catch (e) {
      setPitchErr(e instanceof Error ? e.message : String(e));
    } finally {
      setPitching(null);
    }
  }

  if (loading && grants.length === 0 && available.length === 0) {
    return (
      <article id="agent-desk-offer-card" className="agent-desk-offer-card">
        <div className="agent-desk-offer-eyebrow">OFFERS PRESENTED</div>
        <p className="agent-desk-offer-empty">Loading…</p>
      </article>
    );
  }

  if (err && grants.length === 0 && available.length === 0) {
    return (
      <article id="agent-desk-offer-card" className="agent-desk-offer-card">
        <div className="agent-desk-offer-eyebrow">OFFERS</div>
        <div className="dial-err">{err}</div>
      </article>
    );
  }

  return (
    <article id="agent-desk-offer-card" className="agent-desk-offer-card">
      {/* Presented section — what the AI has already pitched. */}
      <div className="agent-desk-offer-eyebrow">
        OFFERS PRESENTED · {grants.length}
      </div>
      {grants.length === 0 && (
        <p className="agent-desk-offer-empty">
          No offer has been pitched yet on this call.
        </p>
      )}
      {grants.length > 0 && (
        <ul className="agent-desk-offer-list">
          {grants.map((g) => {
            const impact = billImpact(currentMonthlyCents, g);
            return (
            <li
              key={g.id}
              id={`agent-desk-offer-grant-${g.id}`}
              className={`agent-desk-offer-row is-${g.status}`}
            >
              <div className="agent-desk-offer-row-head">
                <span className="agent-desk-offer-row-name">{g.offer_name}</span>
                <span className={`agent-desk-offer-row-status is-${g.status}`}>
                  {STATUS_LABEL[g.status]}
                </span>
              </div>
              <div className="agent-desk-offer-row-meta">
                <span className="agent-desk-offer-row-code">{g.offer_code}</span>
                <span className="agent-desk-offer-row-sep">·</span>
                <span>{describeTerms(g)}</span>
              </div>
              {impact && <BillImpact impact={impact} />}
              <div className="agent-desk-offer-row-times">
                <span>Pitched {fmtTime(g.offered_at)}</span>
                {g.resolved_at && (
                  <>
                    <span className="agent-desk-offer-row-sep">·</span>
                    <span>
                      {STATUS_LABEL[g.status]} {fmtTime(g.resolved_at)}
                    </span>
                  </>
                )}
              </div>
              {g.status === "offered" && (
                <div className="agent-desk-offer-row-actions">
                  <button
                    id={`agent-desk-offer-accept-${g.id}`}
                    type="button"
                    className="agent-desk-offer-resolve agent-desk-offer-resolve-accept"
                    onClick={() => resolveGrant(g.id, "accepted")}
                    disabled={resolving !== null}
                  >
                    {resolving === g.id ? "…" : "Mark accepted"}
                  </button>
                  <button
                    id={`agent-desk-offer-decline-${g.id}`}
                    type="button"
                    className="agent-desk-offer-resolve agent-desk-offer-resolve-decline"
                    onClick={() => resolveGrant(g.id, "rejected")}
                    disabled={resolving !== null}
                  >
                    Mark declined
                  </button>
                </div>
              )}
            </li>
            );
          })}
        </ul>
      )}

      {/* Available section — eligibility-filtered offers the AI hasn't
       * pitched yet. Click "Pitch this" to nudge the AI to surface it
       * on the next assistant turn. */}
      {availableFiltered.length > 0 && (
        <>
          <div className="agent-desk-offer-divider" />
          <div className="agent-desk-offer-eyebrow">
            AVAILABLE TO PITCH · {availableFiltered.length}
          </div>
          <ul className="agent-desk-offer-list">
            {availableFiltered.map((o, i) => {
              const rung = rungAt(i);
              return (
              <li
                key={o.id}
                id={`agent-desk-offer-available-${o.id}`}
                className={`agent-desk-offer-row is-available is-rung-${rung}`}
              >
                <div className="agent-desk-offer-row-head">
                  <span className="agent-desk-offer-row-name">
                    <span className={`agent-desk-offer-rung is-${rung}`}>
                      {RUNG_LABEL[rung]}
                    </span>
                    {o.name}
                  </span>
                  <button
                    id={`agent-desk-offer-pitch-${o.id}`}
                    type="button"
                    className="agent-desk-offer-pitch-btn"
                    onClick={() => pitchOffer(o)}
                    disabled={pitching !== null}
                    title="Nudge the AI to pitch this offer next"
                  >
                    {pitching === o.id ? "Sending…" : "Pitch this"}
                  </button>
                </div>
                <div className="agent-desk-offer-row-meta">
                  <span className="agent-desk-offer-row-code">{o.code}</span>
                  <span className="agent-desk-offer-row-sep">·</span>
                  <span>{describeTerms(o)}</span>
                </div>
                {(() => {
                  const impact = billImpact(currentMonthlyCents, o);
                  return impact ? <BillImpact impact={impact} /> : null;
                })()}
                {o.description && (
                  <div className="agent-desk-offer-row-desc">{o.description}</div>
                )}
              </li>
              );
            })}
          </ul>
        </>
      )}
      {pitchErr && <div className="dial-err">{pitchErr}</div>}
      {resolveErr && <div className="dial-err">{resolveErr}</div>}
    </article>
  );
}
